import {
  AUDIT_TYPE_LABELS,
  AUDIT_TYPES,
  ALERT_AUDIENCE_LABELS,
  ALERT_CATEGORY_LABELS,
  type AuditType,
} from "@/services/api/guardiansv2/types";
import type { SelectOption } from "@/components/ui/select-dropdown";
import type { AuditBucket } from "../audits/useAudits";
import type { ScreeningBucket } from "../screenings/useScreenings";
import type { AuditOriginFilter, GuardiansView } from "../guardiansParams";
import {
  DEFAULT_GUARDIANS_FILTERS,
  type AlertAudienceFilter,
  type AlertCategoryFilter,
  type GuardiansFilterValues,
} from "./GuardiansFilterBar";

export type TabFilterChipId =
  | "auditType"
  | "auditOrigin"
  | "auditBucket"
  | "screeningBucket";

export type ActiveFilterChipId = TabFilterChipId | import("./GuardiansFilterBar").SecondaryFilterKey;

export interface GuardiansTabFilterState {
  auditBucket: AuditBucket;
  auditOrigin: AuditOriginFilter;
  screeningBucket: ScreeningBucket;
}

export const DEFAULT_TAB_FILTER_STATE: GuardiansTabFilterState = {
  auditBucket: "all",
  auditOrigin: "all",
  screeningBucket: "all",
};

const AUDIT_BUCKET_LABELS: Record<AuditBucket, string> = {
  all: "All statuses",
  red: "Red",
  amber: "Amber",
  green: "Green",
  failed: "Failed",
};

const SCREENING_BUCKET_LABELS: Record<ScreeningBucket, string> = {
  all: "All outcomes",
  passed: "Passed",
  flagged: "Alert raised",
  failed: "Failed checks",
};

const AUDIT_ORIGIN_LABELS: Record<AuditOriginFilter, string> = {
  all: "All audits",
  alert: "From alerts",
};

export const AUDIT_TYPE_FILTER_OPTIONS: SelectOption<AuditType | "all">[] = [
  { value: "all", label: "All types" },
  ...AUDIT_TYPES.map((id) => ({ value: id, label: AUDIT_TYPE_LABELS[id] })),
];

export const AUDIT_BUCKET_FILTER_OPTIONS: SelectOption<AuditBucket>[] = Object.entries(
  AUDIT_BUCKET_LABELS
).map(([value, label]) => ({ value: value as AuditBucket, label }));

export const AUDIT_ORIGIN_FILTER_OPTIONS: SelectOption<AuditOriginFilter>[] = Object.entries(
  AUDIT_ORIGIN_LABELS
).map(([value, label]) => ({ value: value as AuditOriginFilter, label }));

export const SCREENING_BUCKET_FILTER_OPTIONS: SelectOption<ScreeningBucket>[] = Object.entries(
  SCREENING_BUCKET_LABELS
).map(([value, label]) => ({ value: value as ScreeningBucket, label }));

/** Whether the main toolbar shows Filters button and active chips. */
export function guardiansFiltersToolbarVisible(view: GuardiansView): boolean {
  return view === "alerts" || view === "audits" || view === "screenings";
}

function scopeFilterCount(values: GuardiansFilterValues): number {
  let n = 0;
  if (values.missionTemplateId) n++;
  if (values.taskId) n++;
  if (values.agentId) n++;
  if (values.catalogueId) n++;
  return n;
}

function alertsSpecificCount(values: GuardiansFilterValues): number {
  let n = 0;
  if (values.alertCategory !== "all") n++;
  if (values.alertAudience !== "all") n++;
  return n;
}

function auditsSpecificCount(
  values: GuardiansFilterValues,
  tab: GuardiansTabFilterState
): number {
  let n = 0;
  if (values.auditType !== "all") n++;
  if (tab.auditOrigin !== "all") n++;
  if (tab.auditBucket !== "all") n++;
  return n;
}

function screeningsSpecificCount(tab: GuardiansTabFilterState): number {
  return tab.screeningBucket !== "all" ? 1 : 0;
}

/** Badge count for the Filters button on the active view only. */
export function guardiansActiveFilterCount(
  view: GuardiansView,
  values: GuardiansFilterValues,
  tab: GuardiansTabFilterState
): number {
  if (!guardiansFiltersToolbarVisible(view)) return 0;
  const scope = scopeFilterCount(values);
  if (view === "alerts") return scope + alertsSpecificCount(values);
  if (view === "audits") return scope + auditsSpecificCount(values, tab);
  if (view === "screenings") return scope + screeningsSpecificCount(tab);
  return 0;
}

export interface ActiveFilterChipDescriptor {
  id: ActiveFilterChipId;
  label: string;
}

export function guardiansActiveFilterChips(
  view: GuardiansView,
  values: GuardiansFilterValues,
  tab: GuardiansTabFilterState,
  labels: {
    taskLabel: (id: string) => string | null | undefined;
    missionTemplateOptionLabel: (id: string) => string;
    agentOptionLabel: (id: string) => string;
    catalogueOptionLabel: (id: string) => string;
  }
): ActiveFilterChipDescriptor[] {
  if (!guardiansFiltersToolbarVisible(view)) return [];

  const chips: ActiveFilterChipDescriptor[] = [];

  if (values.missionTemplateId) {
    chips.push({
      id: "missionTemplateId",
      label: `Mission: ${labels.missionTemplateOptionLabel(values.missionTemplateId)}`,
    });
  }
  if (values.taskId) {
    chips.push({
      id: "taskId",
      label: `Task: ${labels.taskLabel(values.taskId) ?? values.taskId}`,
    });
  }
  if (values.agentId) {
    chips.push({
      id: "agentId",
      label: `Agent: ${labels.agentOptionLabel(values.agentId)}`,
    });
  }
  if (values.catalogueId) {
    chips.push({
      id: "catalogueId",
      label: `Catalogue: ${labels.catalogueOptionLabel(values.catalogueId)}`,
    });
  }

  if (view === "alerts") {
    if (values.alertCategory !== "all") {
      chips.push({
        id: "alertCategory",
        label: `Category: ${ALERT_CATEGORY_LABELS[values.alertCategory]}`,
      });
    }
    if (values.alertAudience !== "all") {
      chips.push({
        id: "alertAudience",
        label: `Audience: ${ALERT_AUDIENCE_LABELS[values.alertAudience]}`,
      });
    }
  }

  if (view === "audits") {
    if (values.auditType !== "all") {
      chips.push({
        id: "auditType",
        label: `Type: ${AUDIT_TYPE_LABELS[values.auditType]}`,
      });
    }
    if (tab.auditOrigin !== "all") {
      chips.push({
        id: "auditOrigin",
        label: AUDIT_ORIGIN_LABELS[tab.auditOrigin],
      });
    }
    if (tab.auditBucket !== "all") {
      chips.push({
        id: "auditBucket",
        label: `Status: ${AUDIT_BUCKET_LABELS[tab.auditBucket]}`,
      });
    }
  }

  if (view === "screenings" && tab.screeningBucket !== "all") {
    chips.push({
      id: "screeningBucket",
      label: `Outcome: ${SCREENING_BUCKET_LABELS[tab.screeningBucket]}`,
    });
  }

  return chips;
}

/** Values to apply when clearing filters from the popover for the current view. */
export function guardiansPopoverResetValues(
  view: GuardiansView,
  values: GuardiansFilterValues
): GuardiansFilterValues {
  const base = { ...values };
  base.missionTemplateId = "";
  base.taskId = "";
  base.agentId = "";
  base.catalogueId = "";
  if (view === "alerts") {
    base.alertCategory = "all";
    base.alertAudience = "all";
  }
  if (view === "audits") {
    base.auditType = "all";
  }
  return base;
}

export type TabFilterUrlPatch = Partial<{
  rating: string | null;
  audit_origin: string | null;
  screening_outcome: string | null;
}>;

/** URL patch for tab-specific filters cleared from the popover. */
export function guardiansPopoverResetTabPatch(view: GuardiansView): TabFilterUrlPatch {
  if (view === "audits") {
    return { rating: null, audit_origin: null };
  }
  if (view === "screenings") {
    return { screening_outcome: null };
  }
  return {};
}

export function isDefaultGuardiansFilters(values: GuardiansFilterValues): boolean {
  return (
    values.auditType === DEFAULT_GUARDIANS_FILTERS.auditType &&
    values.missionTemplateId === DEFAULT_GUARDIANS_FILTERS.missionTemplateId &&
    values.taskId === DEFAULT_GUARDIANS_FILTERS.taskId &&
    values.agentId === DEFAULT_GUARDIANS_FILTERS.agentId &&
    values.catalogueId === DEFAULT_GUARDIANS_FILTERS.catalogueId &&
    values.alertCategory === DEFAULT_GUARDIANS_FILTERS.alertCategory &&
    values.alertAudience === DEFAULT_GUARDIANS_FILTERS.alertAudience
  );
}

export type { AlertCategoryFilter, AlertAudienceFilter };
